import { useId, type JSX } from 'react';

import { Badge } from '@/components/ui/badge.js';
import { Label } from '@/components/ui/label.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.js';
import { useProjectEnvironments } from '@/lib/api/quality.js';

/** Sentinel Select value for "no environment" — Radix forbids an empty-string item value (mirrors the
 *  Zephyr region picker in ProjectSettingsPanel). The reported/stored value is `undefined`. Environment
 *  names are user-defined but validated server-side to reject `/`-containing names, so embedding a `/`
 *  here guarantees this sentinel can never collide with a real environment name. */
const NONE_VALUE = '__none__/sentinel';

interface EnvironmentSelectProps {
  /** Resolves the option list — the project whose settings.environments are offered. */
  projectId: string;
  /** Selected environment NAME; undefined = none. */
  value: string | undefined;
  /** Called with the chosen environment name, or undefined when "None" is picked. */
  onChange: (environmentName: string | undefined) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * A project's environment picker (Block C): a Select ("None" + each configured environment) plus a
 * preview of the selected environment's base URL, plain vars, and secret var KEYS (presence-only —
 * values live in Vault and are never fetched/rendered here). Shared by LaunchRunDialog and
 * RegressionJobDialog so both dialogs show one consistent picker + preview. The three props above are
 * frozen — later consumers depend on this exact shape; `disabled`/`className` may grow, the required
 * three never change.
 */
export function EnvironmentSelect({
  projectId,
  value,
  onChange,
  disabled,
  className,
}: EnvironmentSelectProps): JSX.Element {
  const selectId = useId();
  const { environments, isLoading } = useProjectEnvironments(projectId);
  const selected = environments.find((env) => env.name === value);
  /** A stored name not present in the current option list. Surfaced as its own option rather than
   *  left to render blank. While the project query is still loading we can't yet tell a deleted
   *  environment from one that just hasn't loaded, so it renders as the plain name (neutral); once
   *  loaded, a still-unmatched name is marked "Unknown environment" so the user re-selects it. */
  const unmatchedValue = value !== undefined && !selected ? value : undefined;
  const hasPreviewContent =
    selected &&
    (Boolean(selected.baseUrl) ||
      Object.keys(selected.vars ?? {}).length > 0 ||
      (selected.secretVarKeys?.length ?? 0) > 0);

  return (
    <div className={className}>
      <div className="space-y-1.5">
        <Label htmlFor={selectId}>Environment</Label>
        <Select
          value={value ?? NONE_VALUE}
          onValueChange={(next) =>
            onChange(next === NONE_VALUE ? undefined : next)
          }
          disabled={disabled}
        >
          <SelectTrigger id={selectId} className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE_VALUE}>None</SelectItem>
            {unmatchedValue !== undefined && (
              <SelectItem value={unmatchedValue}>
                {isLoading
                  ? unmatchedValue
                  : `Unknown environment (${unmatchedValue})`}
              </SelectItem>
            )}
            {environments.map((env) => (
              <SelectItem key={env.name} value={env.name}>
                {env.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selected ? (
        <div className="mt-2 space-y-2 rounded-md border border-dashed border-border p-3 text-xs">
          {selected.baseUrl ? (
            <p>
              <span className="font-medium text-foreground">Base URL: </span>
              <span className="text-muted-foreground">{selected.baseUrl}</span>
            </p>
          ) : null}
          {selected.vars && Object.keys(selected.vars).length > 0 ? (
            <div className="space-y-1">
              <p className="font-medium text-foreground">Variables</p>
              <ul className="space-y-0.5">
                {Object.entries(selected.vars).map(([key, val]) => (
                  <li key={key} className="text-muted-foreground">
                    <span className="font-mono">{key}</span> = {val}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {selected.secretVarKeys && selected.secretVarKeys.length > 0 ? (
            <div className="space-y-1">
              <p className="font-medium text-foreground">Secrets</p>
              <div className="flex flex-wrap gap-1">
                {selected.secretVarKeys.map((key) => (
                  <Badge key={key} variant="secondary">
                    {key} · set
                  </Badge>
                ))}
              </div>
            </div>
          ) : null}
          {!hasPreviewContent ? (
            <p className="italic text-muted-foreground">
              No base URL, variables, or secrets set.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
